// src/oura/components/DeleteBusinessModal.tsx
//
// Destructive-confirm for removing a business from the pipeline. Replaces a
// bare window.confirm(), which could not be styled, could not show the domain
// as anything but plain text, and looks like a browser error to a client
// sitting next to you.
//
// Follows the platform's destructive-dialog pattern (see
// src/containers/dialog/dialog-delete-job-confirm.tsx): red circular icon,
// question as the title, consequence as body copy, Cancel + destructive action
// side by side. Built on the Oura modal shell rather than @component/dialog so
// it matches the other Oura modals (NewScanModal) — same overlay, same radius,
// same z-index.
//
// Type-to-confirm, as the job dialog does, with the business's NAME as the
// word to type. This was a soft delete once (the row was marked and a re-scan
// revived it), and a click was enough for that. Since ourteam-backend#5338 it
// purges every row and file the business owns, so the button stays off until
// the name is typed: a list of dozens of rows is easy to click the wrong one
// in, and typing the name is also the check that this is the right one.

import { useEffect, useState } from "react";
import { AlertTriangleIcon, Loader2Icon, Trash2Icon } from "lucide-react";
import { Input } from "@component/input";
import { Button } from "../primitives/Button";

/** Typed against the name: case and surrounding spaces do not matter, the letters do. */
const same = (typed: string, name: string) => typed.trim().toLowerCase() === name.trim().toLowerCase();

export function DeleteBusinessModal({
    open,
    businessId,
    businessName,
    domain,
    deleting,
    scanRunning,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    /** The business asked about: what was typed belongs to it, and to no other. */
    businessId: string;
    businessName: string;
    domain: string;
    /** Disables both buttons and spins the destructive one. */
    deleting: boolean;
    /**
     * A scan for this business is still in flight.
     *
     * Removing the business does NOT stop it: the delete marks the business
     * row, while the scan lives in a separate record and keeps running — and
     * keeps spending. The domain also stays locked, so a replacement scan is
     * refused until the original finishes. Silence here sent an operator down
     * exactly that path.
     */
    scanRunning?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    // What was typed is kept with the business it was typed for. A different business reads as empty at
    // once, before any effect runs, so a name typed for one can never switch the delete on for another
    // (a same-named business included); a reopened dialog starts empty too.
    const [confirmation, setConfirmation] = useState(() => ({ businessId, typed: "" }));
    useEffect(() => {
        setConfirmation({ businessId, typed: "" });
    }, [open, businessId]);
    const typed = confirmation.businessId === businessId ? confirmation.typed : "";
    const confirmed = businessName.trim() !== "" && same(typed, businessName);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-business-title"
            onKeyDown={(e) => {
                if (e.key === "Escape" && !deleting) onCancel();
                if (e.key === "Enter" && confirmed && !deleting && (e.target as HTMLElement).tagName === "INPUT") onConfirm();
            }}
        >
            {/* No dismiss-on-backdrop-click while the request is in flight: the
                row would vanish from under a half-finished action. */}
            <div
                className="absolute inset-0 bg-gray-900/50"
                onClick={() => !deleting && onCancel()}
            />

            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-start gap-3 px-6 pt-5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
                        <AlertTriangleIcon className="h-5 w-5 text-rose-600" />
                    </div>
                    <div className="min-w-0 pt-1.5">
                        <h2 id="delete-business-title" className="text-base font-semibold text-gray-900">
                            Delete this business?
                        </h2>
                    </div>
                </div>

                <div className="space-y-3 px-6 pb-1 pt-3">
                    {/* A running scan is the one case where removing the business
                        does not do what it looks like it does. */}
                    {scanRunning && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                            <p className="text-sm font-medium text-amber-900">
                                A scan is still running for this business.
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-amber-800">
                                Deleting it now makes that scan fail: its next write finds no
                                business to write to. Let the scan finish first if you want its
                                result.
                            </p>
                        </div>
                    )}
                    {/* The loss FIRST, and named. This was a soft delete until
                        ourteam-backend#5338: the row was marked, the history stayed,
                        and a re-scan revived it — which is what this dialog used to
                        promise. `purge_business` now deletes every row the business
                        owns and `purge_objects` the files behind them, so the old
                        reassurance would be the most dangerous sentence on the
                        screen. */}
                    <p className="text-sm leading-relaxed text-gray-900">
                        <span className="font-medium">{businessName}</span>{" "}
                        <span className="text-gray-500">({domain})</span> and everything Oura
                        holds for it will be deleted: its scans and scores, its documents and
                        delivery record, and the files uploaded for it.{" "}
                        <span className="font-medium">This cannot be undone.</span>
                    </p>
                    {/* What a staffer will ask next, in the order they ask it:
                        "can I get it back?" then "did I just delete the client?" */}
                    <p className="text-sm leading-relaxed text-gray-600">
                        Scanning the same domain again starts a brand-new business, not this one.
                    </p>
                    <p className="text-sm leading-relaxed text-gray-600">
                        The client&rsquo;s own workspace and logins are not touched, and nothing
                        in their Google Drive is deleted.
                    </p>
                </div>

                {/* Typing the name is what turns the delete button on. Focus starts in
                    this field, never on the destructive button, and Enter in it
                    confirms only once the name matches. */}
                <div className="space-y-2 px-6 pt-3">
                    <label htmlFor="delete-business-confirm" className="text-sm font-medium text-gray-900">
                        Type <strong>{businessName}</strong> to confirm
                    </label>
                    <Input
                        id="delete-business-confirm"
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                        value={typed}
                        disabled={deleting}
                        onChange={(e) => setConfirmation({ businessId, typed: e.target.value })}
                        placeholder={businessName}
                    />
                </div>

                <div className="flex gap-2 px-6 pb-5 pt-4">
                    <Button
                        variant="outline"
                        className="flex-1"
                        disabled={deleting}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        className="flex-1"
                        disabled={deleting || !confirmed}
                        onClick={onConfirm}
                    >
                        {deleting ? (
                            <>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            <>
                                <Trash2Icon className="h-4 w-4" />
                                Delete business
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
